import { type FC, useState } from "react";

import {
  type ProjectFragment,
  type TranscriptDocumentFragment,
} from "@app_schema";

import {
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
  M4A_EXTENSION,
  M4A_MIME_TYPE,
  MP3_EXTENSION,
  MP3_MIME_TYPE,
  MP4_EXTENSION,
  MP4_MIME_TYPE,
  WAV_EXTENSION,
  WAV_MIME_TYPE,
} from "@application/utilities/file";

import { FileManager } from "@styled/file_manager";
import { FilePicker } from "@styled/file_picker";
import { FilePickerGuide } from "@styled/file_picker_guide";
import { FileQueue } from "@styled/file_queue";
import { TranscriptLayout } from "@styled/transcript_sheet";

import { IntakePane } from "./intake_pane";
import { renderFileQueueItem } from "./shared_card_utils";
import { TranscriptFiles } from "./transcript_files";
import { TranscriptReader } from "./transcript_reader";

// One drop zone for both inputs — the file extension is the choice, so the
// server routes audio (MP3/WAV/M4A/MP4) to transcription and .docx to cleanup.
export const TRANSCRIPT_ACCEPT = [
  MP3_EXTENSION,
  MP3_MIME_TYPE,
  WAV_EXTENSION,
  WAV_MIME_TYPE,
  M4A_EXTENSION,
  M4A_MIME_TYPE,
  MP4_EXTENSION,
  MP4_MIME_TYPE,
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
].join(",");

// The "Dictation & notes" section: until a file is in, the upload guide; then
// the selected file's transcript with every file in a rail beside it.
export const TranscriptsPane: FC<{
  project: Pick<ProjectFragment, "id">;
  documents: TranscriptDocumentFragment[];
  openingId?: string | null;
  save: (blob: { signed_id: string }) => Promise<void>;
}> = ({ project, documents, openingId, save }) => {
  const [selectedId, setSelectedId] = useState<string>();
  // Until one is picked, the file the server opens the section on.
  const selected =
    documents.find((document) => document.id === selectedId) ??
    documents.find((document) => document.id === openingId);

  return (
    <FileManager save={save}>
      {selected ? (
        <TranscriptLayout
          rail={
            <TranscriptFiles
              documents={documents}
              selectedId={selected.id}
              accept={TRANSCRIPT_ACCEPT}
              onSelect={setSelectedId}
            />
          }
        >
          <TranscriptReader projectId={project.id} document={selected} />
        </TranscriptLayout>
      ) : (
        <IntakePane
          title="Dictation & notes"
          subtitle="Turn audio or notes into a clean transcript."
        >
          <FilePicker
            title="Upload audio or dictation notes"
            action="Upload audio or notes"
            accept={TRANSCRIPT_ACCEPT}
          >
            <FilePickerGuide
              steps={[
                {
                  label: "Upload audio or notes",
                  description: "MP3, WAV, M4A, MP4, or DOCX",
                },
                {
                  label: "We transcribe and clean",
                  description: "Audio → text, then report-style cleanup",
                },
                {
                  label: "Download Word transcript",
                  description: "DOCX ready in your project",
                },
              ]}
            />
          </FilePicker>
          <FileQueue render={renderFileQueueItem} />
        </IntakePane>
      )}
    </FileManager>
  );
};
